import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  MapPin, Users, BedDouble, Bath, Clock, ChevronLeft,
  Wifi, Car, Coffee, Tv, Wind, Utensils, Waves, Shirt,
  Loader2, SquareIcon
} from "lucide-react";
import { Helmet } from "react-helmet-async";
import axios from "axios";
import { toast } from "sonner";
import { useLanguage } from "@/lib/LanguageContext";
import Navbar from "@/components/Navbar";
import PhotoGallery from "@/components/PhotoGallery";
import DetailMap from "@/components/DetailMap";
import Reveal from "@/components/Reveal";
import Footer from "@/components/Footer";
import HostawayCalendarWidget from "@/components/HostawayCalendarWidget";
import { LodgingBusinessSchema, BreadcrumbSchema } from "@/components/seo/StructuredData";
import { getViennaLocation } from "@/lib/viennaLocation";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AMENITY_ICONS = {
  "WiFi": Wifi, "Internet": Wifi, "WLAN": Wifi,
  "Parking": Car, "Parkplatz": Car, "Free parking": Car,
  "Kitchen": Utensils, "Küche": Utensils,
  "TV": Tv, "Fernseher": Tv, "Cable TV": Tv,
  "Air conditioning": Wind, "Klimaanlage": Wind,
  "Coffee maker": Coffee, "Kaffeemaschine": Coffee,
  "Pool": Waves, "Schwimmbad": Waves,
  "Washer": Shirt, "Waschmaschine": Shirt,
};

function getAmenityIcon(name) {
  for (const [key, Icon] of Object.entries(AMENITY_ICONS)) {
    if (name.toLowerCase().includes(key.toLowerCase())) return Icon;
  }
  return SquareIcon;
}

export default function ListingDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [requestVersion, setRequestVersion] = useState(0);
  const { t, lang } = useLanguage();

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setLoadError(false);
    const fetchListing = async () => {
      try {
        const res = await axios.get(`${API}/listings/${id}`, { signal: controller.signal });
        setListing(res.data);
      } catch {
        if (!controller.signal.aborted) {
          setListing(null);
          setLoadError(true);
          toast.error("Unterkunft konnte nicht geladen werden");
        }
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    };
    fetchListing();
    return () => controller.abort();
  }, [id, requestVersion]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center">
        <Navbar />
        <Loader2 className="w-8 h-8 animate-spin text-[#86868B]" />
      </div>
    );
  }

  if (!listing) {
    return (
      <div className="min-h-screen bg-[#FAFAFA] flex flex-col items-center justify-center">
        <Navbar />
        <p data-testid="listing-load-message" role={loadError ? "alert" : undefined} className="text-xl text-[#86868B]">
          {loadError ? t("detail_load_error") : t("detail_not_found")}
        </p>
        {loadError && (
          <Button
            data-testid="listing-retry-button"
            onClick={() => setRequestVersion((version) => version + 1)}
            className="mt-4 rounded-full"
          >
            {t("listings_retry")}
          </Button>
        )}
        <Button data-testid="listing-back-to-listings-button" onClick={() => navigate("/unterkuenfte")} variant="outline" className="mt-3 rounded-full">
          {t("detail_back_to_listings")}
        </Button>
      </div>
    );
  }

  const publicAddress = `${lang === "de" ? "Wien" : "Vienna"}${listing.district ? `, ${listing.district}` : ""} – ${lang === "de" ? "genaue Adresse nach Buchung" : "exact address after booking"}`;
  // Preserve the existing Ottakring area information without receiving a street address.
  const location = listing.districtNumber === 16 ? getViennaLocation("redtenbachergasse") : null;

  return (
    <div data-testid="listing-detail-page" className="min-h-screen bg-[#FAFAFA]">
      {listing && (
        <>
          <Helmet>
            <title>{`${listing.name} in Wien | Oona Flats`}</title>
            <meta name="description" content={`${listing.name} in ${listing.city || "Wien"}, ${listing.bedroomsNumber} Schlafzimmer für bis zu ${listing.personCapacity} Gäste ab ${listing.price} EUR/Nacht. Sofort online buchen mit sicherer Stripe-Zahlung bei Oona Flats.`} />
            <meta property="og:title" content={`${listing.name} | Oona Flats Wien`} />
            <meta property="og:description" content={listing.description?.substring(0, 160) || ""} />
            <meta property="og:type" content="product" />
            <meta property="og:url" content={`https://oonaflats.com/unterkunft/${listing.id}`} />
            {listing.thumbnailUrl && <meta property="og:image" content={listing.thumbnailUrl} />}
            <meta name="twitter:card" content="summary_large_image" />
            <link rel="canonical" href={`https://oonaflats.com/unterkunft/${listing.id}`} />
          </Helmet>
          <LodgingBusinessSchema listing={listing} />
          <BreadcrumbSchema items={[
            { name: "Startseite", path: "/" },
            { name: "Unterkünfte", path: "/unterkuenfte" },
            { name: listing.name, path: `/unterkunft/${listing.id}` },
          ]} />
        </>
      )}
      <Navbar />

      <div className="pt-24 pb-24 px-6 md:px-12 lg:px-24">
        <div className="max-w-7xl mx-auto">
          {/* Back button */}
          <button
            data-testid="back-button"
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-sm text-[#86868B] hover:text-[#1D1D1F] transition-colors mb-8"
          >
            <ChevronLeft className="w-4 h-4" />
            Zurück
          </button>

          {/* Photo Gallery */}
          <PhotoGallery images={listing.images} name={listing.name} />

          {/* Content Grid */}
          <div className="mt-10 grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_400px] gap-12">
            {/* Left: Details */}
            <div className="min-w-0 space-y-10">
              {/* Title & Location */}
              <Reveal>
                <div>
                  <h1
                    data-testid="listing-title"
                    className="text-3xl md:text-5xl font-semibold tracking-tight text-[#1D1D1F]"
                    style={{ fontFamily: 'Outfit, sans-serif' }}
                  >
                    {listing.name}
                  </h1>
                  <div className="flex items-center gap-2 mt-3 text-[#86868B]">
                    <MapPin className="w-4 h-4" />
                    <span data-testid="listing-public-address" className="text-base">{publicAddress}</span>
                  </div>
                </div>
              </Reveal>

              {/* Quick Stats */}
              <Reveal delay={120}>
                <div className="flex flex-wrap gap-6">
                <div className="flex items-center gap-3 bg-white rounded-2xl border border-black/5 px-5 py-3">
                  <Users className="w-5 h-5 text-[#86868B]" />
                  <div>
                    <p className="text-xs text-[#86868B]">{t("detail_guests")}</p>
                    <p className="font-semibold text-[#1D1D1F]">{listing.personCapacity}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 bg-white rounded-2xl border border-black/5 px-5 py-3">
                  <BedDouble className="w-5 h-5 text-[#86868B]" />
                  <div>
                    <p className="text-xs text-[#86868B]">{t("detail_bedrooms")}</p>
                    <p className="font-semibold text-[#1D1D1F]">{listing.bedroomsNumber}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 bg-white rounded-2xl border border-black/5 px-5 py-3">
                  <Bath className="w-5 h-5 text-[#86868B]" />
                  <div>
                    <p className="text-xs text-[#86868B]">{t("detail_bathrooms")}</p>
                    <p className="font-semibold text-[#1D1D1F]">{listing.bathroomsNumber}</p>
                  </div>
                </div>
                {listing.squareMeters && (
                  <div className="flex items-center gap-3 bg-white rounded-2xl border border-black/5 px-5 py-3">
                    <SquareIcon className="w-5 h-5 text-[#86868B]" />
                    <div>
                      <p className="text-xs text-[#86868B]">{t("detail_area")}</p>
                      <p className="font-semibold text-[#1D1D1F]">{listing.squareMeters} m²</p>
                    </div>
                  </div>
                )}
                </div>
              </Reveal>

              <Separator className="bg-black/5" />

              {/* Description */}
              <Reveal>
                <div>
                  <h2 className="text-2xl font-medium tracking-tight text-[#1D1D1F] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    {t("detail_description")}
                  </h2>
                  <p className="text-base text-[#1D1D1F] leading-relaxed whitespace-pre-line">
                    {listing.description}
                  </p>
                </div>
              </Reveal>

              {/* Lage & Anbindung — Bezirk + ÖPNV + Umgebung (SEO/AEO long-tail content) */}
              {location && (
                <Reveal>
                  <div data-testid="location-section" className="bg-white rounded-3xl border border-black/5 p-6 md:p-8">
                    <p className="text-xs font-semibold tracking-[0.15em] uppercase text-[#86868B] mb-2">
                      Lage & Anbindung
                    </p>
                    <h2 className="text-2xl font-medium tracking-tight text-[#1D1D1F] mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                      {location.district}
                    </h2>
                    <p className="text-base text-[#1D1D1F] leading-relaxed mb-6">
                      {location.description}
                    </p>

                    <h3 className="text-sm font-semibold text-[#1D1D1F] mb-3 uppercase tracking-wider">
                      Öffentliche Verkehrsmittel in Gehweite
                    </h3>
                    <ul data-testid="transit-list" className="space-y-2 mb-6">
                      {location.transit.map((stop) => (
                        <li key={stop.name} className="flex items-center gap-3 text-sm text-[#1D1D1F]">
                          <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-[#1D1D1F] text-white text-[10px] font-bold shrink-0">
                            {stop.type === "Metro" ? stop.name.split(" ")[0] : "TRAM"}
                          </span>
                          <span className="flex-1">
                            <strong className="text-[#1D1D1F]">{stop.name}</strong>
                            <span className="text-[#86868B]"> · {stop.walkMinutes} Min. Fußweg</span>
                          </span>
                        </li>
                      ))}
                    </ul>

                    <h3 className="text-sm font-semibold text-[#1D1D1F] mb-3 uppercase tracking-wider">
                      In der Umgebung
                    </h3>
                    <ul className="space-y-2 text-sm text-[#1D1D1F]/80 leading-relaxed">
                      {location.nearby.map((spot) => (
                        <li key={spot} className="flex gap-2">
                          <span className="text-[#86868B]">·</span>
                          <span>{spot}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </Reveal>
              )}

              {/* Amenities */}
              {listing.amenities?.length > 0 && (
                <Reveal>
                  <div>
                    <h2 className="text-2xl font-medium tracking-tight text-[#1D1D1F] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
                      {t("detail_amenities")}
                    </h2>
                    <div data-testid="amenities-grid" className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      {listing.amenities.slice(0, 12).map((amenity, i) => {
                        const Icon = getAmenityIcon(amenity);
                        return (
                          <div key={`${amenity}-${i}`} className="flex items-center gap-3 bg-white rounded-xl border border-black/5 px-4 py-3">
                            <Icon className="w-4 h-4 text-[#86868B] shrink-0" />
                            <span className="text-sm text-[#1D1D1F] truncate">{amenity}</span>
                          </div>
                        );
                      })}
                    </div>
                    {listing.amenities.length > 12 && (
                      <p className="mt-3 text-sm text-[#86868B]">
                        +{listing.amenities.length - 12} {t("detail_amenities_more")}
                      </p>
                    )}
                  </div>
                </Reveal>
              )}

              {/* Check-in/out */}
              <div className="flex flex-wrap gap-6">
                {listing.checkInTimeStart && (
                  <div className="flex items-center gap-3">
                    <Clock className="w-5 h-5 text-[#86868B]" />
                    <div>
                      <p className="text-xs text-[#86868B]">Check-in</p>
                      <p className="font-medium text-[#1D1D1F]">
                        ab {listing.checkInTimeStart}:00{listing.checkInTimeEnd ? ` bis ${listing.checkInTimeEnd}:00` : ""}
                      </p>
                    </div>
                  </div>
                )}
                {listing.checkOutTime && (
                  <div className="flex items-center gap-3">
                    <Clock className="w-5 h-5 text-[#86868B]" />
                    <div>
                      <p className="text-xs text-[#86868B]">Check-out</p>
                      <p className="font-medium text-[#1D1D1F]">bis {listing.checkOutTime}:00</p>
                    </div>
                  </div>
                )}
              </div>

              {/* House Rules */}
              {listing.houseRules && (
                <div>
                  <h2 className="text-2xl font-medium tracking-tight text-[#1D1D1F] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    {t("detail_house_rules")}
                  </h2>
                  <p className="text-base text-[#1D1D1F] leading-relaxed whitespace-pre-line">
                    {listing.houseRules}
                  </p>
                </div>
              )}

              {/* Location Map */}
              {listing.lat && listing.lng && (
                <div>
                  <h2 className="text-2xl font-medium tracking-tight text-[#1D1D1F] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    {t("detail_location")}
                  </h2>
                  <DetailMap lat={listing.lat} lng={listing.lng} name={listing.name} />
                  <div className="mt-3 flex items-center gap-2 text-sm text-[#86868B]">
                    <MapPin className="w-4 h-4 shrink-0" />
                    <span data-testid="listing-map-address">{publicAddress}</span>
                  </div>
                </div>
              )}
            </div>

            {/* Right: Booking Card with Hostaway Widget */}
            <div className="min-w-0">
              <div
                data-testid="booking-card"
                className="sticky top-28 bg-white rounded-3xl border border-black/5 p-3 sm:p-6 shadow-[0_16px_40px_rgba(0,0,0,0.08)]"
              >
                <div className="flex items-baseline gap-2 mb-6">
                  <span className="text-3xl font-semibold text-[#1D1D1F] tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    {listing.price} EUR
                  </span>
                  <span className="text-[#86868B] text-sm">/ Nacht</span>
                </div>

                {/* Hostaway Calendar + Checkout Widget */}
                <div className="w-full [&_div]:max-w-full [&_div]:min-w-0 [&_button]:whitespace-normal [&_[class*='ButtonContainer']]:flex [&_[class*='ButtonContainer']]:flex-wrap [&_[class*='Grid']]:grid-cols-7 [&_.CalendarDay]:w-full [&_[class*='Weekday']]:w-full">
                  <HostawayCalendarWidget listingId={listing.id} lang={lang} />
                </div>

                {listing.minNights > 1 && (
                  <p className="text-xs text-center text-[#86868B] mt-4">
                    {t("detail_min_nights")}: {listing.minNights} {lang === "de" ? "Nächte" : "nights"}
                  </p>
                )}

                <p className="text-[11px] text-center text-[#86868B]/70 mt-3 leading-relaxed">
                  {lang === "de"
                    ? "Buchung & Zahlung erfolgen sicher über unseren Partner Hostaway."
                    : "Booking & payment are processed securely via our partner Hostaway."}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
